import React from 'react';
import { Plus, TrendingUp, Paperclip } from 'lucide-react';
import type { TransparencyTimelineUpdate } from '@yawi/types';
import { useFileViewer } from '@/app/providers';
import { useDateFormatter } from '@/shared/hooks';
import { Button } from '../../../../presentation/components/atoms/Button';

const STATUS_LABEL: Record<string, string> = {
  PLANNING: 'Planeación',
  APPROVED: 'Aprobado',
  IN_PROGRESS: 'En progreso',
  PAUSED: 'Pausado',
  DELAYED: 'Retrasado',
  COMPLETED: 'Completado',
  CANCELLED: 'Cancelado',
};

interface ProjectTimelineProps {
  updates: TransparencyTimelineUpdate[];
  canAddUpdate: boolean;
  onAddUpdate: () => void;
}

/**
 * Bitácora de obra: cada entrada es un momento del proyecto con su texto y sus
 * fotos. Es lo que convierte al proyecto en una historia que el residente puede
 * seguir, en vez de una ficha estática.
 */
export const ProjectTimeline: React.FC<ProjectTimelineProps> = ({
  updates,
  canAddUpdate,
  onAddUpdate,
}) => {
  const { formatDateLong, formatRelativeTime } = useDateFormatter();
  const { openFile, openFileByUrl } = useFileViewer();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-slate-800 flex items-center gap-2">
          <TrendingUp size={16} /> Bitácora
        </h4>
        {canAddUpdate && (
          <Button type="button" variant="secondary" onClick={onAddUpdate} icon={<Plus size={14} />}>
            Agregar actualización
          </Button>
        )}
      </div>

      {updates.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-xl">
          <p className="text-sm text-slate-400">Aún no hay actualizaciones.</p>
          {canAddUpdate && (
            <p className="text-xs text-slate-400 mt-1">
              Publicá la primera para empezar a contar la historia del proyecto.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-5 border-l-2 border-slate-100 pl-5">
          {updates.map((u) => (
            <div key={u.id} className="relative">
              <span className="absolute -left-[27px] top-1.5 w-3 h-3 rounded-full bg-primary-500 ring-4 ring-white" />

              <div className="flex items-baseline gap-2 flex-wrap">
                <p className="text-sm font-medium text-slate-800">{u.title}</p>
                <span className="text-xs text-slate-400" title={formatDateLong(u.createdAt)}>
                  {formatRelativeTime(u.createdAt)}
                </span>
              </div>

              {u.description && (
                <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{u.description}</p>
              )}

              {u.images.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {u.images.map((img) => (
                    <button
                      key={img.id}
                      type="button"
                      onClick={() =>
                        img.url
                          ? openFileByUrl(img.url, u.title, 'image/jpeg')
                          : openFile(img.fileId, u.title, 'image/jpeg')
                      }
                      className="block"
                    >
                      <img
                        src={img.url ?? undefined}
                        alt={u.title}
                        className="w-24 h-24 object-cover rounded-lg border border-slate-200 hover:opacity-90 transition-opacity"
                      />
                    </button>
                  ))}
                </div>
              )}

              {u.documents.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {u.documents.map((doc) => (
                    <button
                      key={doc.id}
                      type="button"
                      onClick={() => openFile(doc.fileId, doc.label ?? 'Documento', 'application/pdf')}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-50 hover:bg-slate-100 text-xs text-slate-600 transition-colors"
                    >
                      <Paperclip size={12} /> {doc.label ?? 'Documento'}
                    </button>
                  ))}
                </div>
              )}

              {(u.progressChange !== null && u.progressChange !== undefined) || u.statusChange ? (
                <div className="flex items-center gap-2 mt-2">
                  {u.progressChange !== null && u.progressChange !== undefined && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                      Avance → {u.progressChange}%
                    </span>
                  )}
                  {u.statusChange && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700">
                      {STATUS_LABEL[u.statusChange] ?? u.statusChange}
                    </span>
                  )}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
